import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Switch } from '@material-ui/core';
import I18n from 'i18n-js';

// @ts-ignore lib
import { logout } from '@lootboy/lib/actions/AuthActions';

import { Divider } from '~/components/atoms/Divider';
import LoginHeader from '~/components/LoginHeader';
import { ANIMATED_ROOT_POPUP_ID } from '~/components/molecules/BottomSheetDialog/utils';
import { CardPopupContainer } from '~/components/molecules/CardPopupContainer';

import { POPUP_OVERLAY_ID } from '~/config';
import useBooleanState from '~/hooks/useBooleanState';
import { useClosePopup } from '~/hooks/useClosePopup';
import { LanguageSelectDialog } from '~/popups/MenuPopup/LanguageSelectDialog';
import { MenuHeader } from '~/popups/MenuPopup/MenuHeader';
import { MenuNavigationBar } from '~/popups/MenuPopup/MenuNavigationBar';
import { MenuOptionsGroup } from '~/popups/MenuPopup/MenuOptionsGroup';
import { MenuOption } from '~/popups/MenuPopup/MenuOptionsGroup/MenuOption';
import { setMute } from '~/redux/actions/SoundActions';
import { useLoggedIn } from '~/redux/hooks/useLoggedIn';
import { useSocialMedia } from '~/redux/hooks/useSocialMedia';
import { languageSelector, muteSelector } from '~/redux/selectors';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import {
  ALL_TUTORIALS_PATH,
  CARDS_PATH,
  CONTACT_PATH,
  LEGAL_PATH,
  PRIVACY_POLICY_PATH,
  REDEEM_CODE_PATH,
  TERMS_PATH,
  USER_DETAILS_PATH,
  VERSION_PATH,
  WITHDRAWAL_PATH,
} from '~/routes/paths';

import { ReactComponent as CardsIcon } from '~/assets/images/iconsLibrary/cards.svg';
import { ReactComponent as GiftIcon } from '~/assets/images/iconsLibrary/gift.svg';
import { ReactComponent as GlobeIcon } from '~/assets/images/iconsLibrary/globe.svg';
import { ReactComponent as HelpCircleIcon } from '~/assets/images/iconsLibrary/help-circle.svg';
import { ReactComponent as InfoCircleIcon } from '~/assets/images/iconsLibrary/info-circle.svg';
import { ReactComponent as Logout } from '~/assets/images/iconsLibrary/logout.svg';
import { ReactComponent as SpeakerIcon } from '~/assets/images/iconsLibrary/speaker.svg';
import { ReactComponent as UserIcon } from '~/assets/images/iconsLibrary/user.svg';

import styles from './MenuView.module.scss';

export function MenuPopup(): JSX.Element {
  const closePopup = useClosePopup();
  const dispatch = useAppDispatch();
  const mute = useSelector(muteSelector);
  const language = useSelector(languageSelector);
  const isLoggedIn = useLoggedIn();
  const socialMedia = useSocialMedia();
  const [
    isLanguageDialogOpened,
    { setTrue: openLanguageDialog, setFalse: closeLanguageDialog },
  ] = useBooleanState(false);

  return (
    <>
      {isLanguageDialogOpened && (
        <LanguageSelectDialog onClose={closeLanguageDialog} />
      )}
      <CardPopupContainer
        className={styles.container}
        onClose={closePopup}
        contentElementId={ANIMATED_ROOT_POPUP_ID}
      >
        <MenuNavigationBar onClose={closePopup} />
        {isLoggedIn ? <MenuHeader /> : <LoginHeader />}
        {isLoggedIn && (
          <MenuOptionsGroup
            label={I18n.t('MenuView_loot')}
            className={styles.loggedInOptions}
          >
            <MenuOption
              label={I18n.t('MenuView_inventory')}
              icon={<CardsIcon className={styles.icon} />}
              to={CARDS_PATH}
            />
            <Divider className={styles.optionDivider} />
            <MenuOption
              label={I18n.t('MenuView_redeem')}
              icon={<GiftIcon className={styles.icon} />}
              to={REDEEM_CODE_PATH}
            />
          </MenuOptionsGroup>
        )}
        <div className={styles.options}>
          <MenuOptionsGroup label={I18n.t('MenuView_settings')}>
            {isLoggedIn && (
              <>
                <MenuOption
                  label={I18n.t('MenuView_profile')}
                  icon={<UserIcon className={styles.icon} />}
                  to={USER_DETAILS_PATH}
                />
                <Divider className={styles.optionDivider} />
              </>
            )}
            <MenuOption
              onClick={openLanguageDialog}
              label={I18n.t('MenuView_setting_language')}
              icon={<GlobeIcon />}
            >
              <span className={styles.language}>
                {I18n.t(`language_${language}`)}
              </span>
            </MenuOption>
            <Divider className={styles.optionDivider} />
            <MenuOption
              label={I18n.t('MenuView_setting_sound')}
              icon={<SpeakerIcon />}
            >
              <Switch
                className={styles.switch}
                checked={!mute}
                onChange={() => {
                  dispatch(setMute());
                }}
              />
            </MenuOption>
          </MenuOptionsGroup>
          <MenuOptionsGroup label={I18n.t('MenuView_help')}>
            <MenuOption
              label={I18n.t('MenuView_help_tutorials')}
              icon={<InfoCircleIcon className={styles.icon} />}
              to={ALL_TUTORIALS_PATH}
            />
            <Divider className={styles.optionDivider} />
            <MenuOption
              label={I18n.t('MenuView_help_contact')}
              icon={<HelpCircleIcon className={styles.icon} />}
              to={CONTACT_PATH}
            />
          </MenuOptionsGroup>
          <MenuOptionsGroup label={I18n.t('MenuView_socials')}>
            {!!socialMedia &&
              socialMedia.map(({ Icon, url, label }, ndx) => (
                <Fragment key={label}>
                  <MenuOption
                    label={I18n.t(label)}
                    icon={<Icon className={styles.socialMediaIcon} />}
                    href={url}
                  />
                  {ndx + 1 !== socialMedia.length && (
                    <Divider className={styles.optionDivider} />
                  )}
                </Fragment>
              ))}
          </MenuOptionsGroup>
          <section className={styles.appLinks}>
            <Divider />
            <MenuOption
              to={LEGAL_PATH}
              label={I18n.t('Footer_label_legal')}
            />
            <Divider />
            <MenuOption
              to={TERMS_PATH}
              label={I18n.t('Footer_label_terms')}
            />
            <Divider />
            <MenuOption
              to={PRIVACY_POLICY_PATH}
              label={I18n.t('Footer_label_privacy')}
            />
            <Divider />
            <MenuOption
              to={WITHDRAWAL_PATH}
              label={I18n.t('Footer_label_withdrawal')}
            />
            <Divider />
            <MenuOption
              to={VERSION_PATH}
              label={I18n.t('MenuView_button_version')}
            />
            <Divider />
            <MenuOption
              onClick={() => {
                window.UC_UI?.showSecondLayer?.();
              }}
              label={I18n.t('Footer_label_privacysettings')}
            />
            <Divider />
          </section>
          {isLoggedIn && (
            <section className={styles.authOptions}>
              <Divider />
              <MenuOption
                label={I18n.t('MenuView_logout')}
                onClick={() => {
                  const popupOverlay =
                    document.getElementById(POPUP_OVERLAY_ID);
                  if (popupOverlay) {
                    popupOverlay.scrollTo(0, 0);
                  }
                  dispatch(logout());
                }}
                className={styles.logout}
              >
                <Logout className={styles.logoutIcon} />
              </MenuOption>
              <Divider />
            </section>
          )}
        </div>
      </CardPopupContainer>
    </>
  );
}
